import { ArrowUpToLine, Loader2, X } from "lucide-react";

import { StatusPill } from "@/components/status-pill";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  type AdminAccountPermissions,
  type AdminCapability,
  type AdminGrantState,
  type AdminPermissionUpdate,
  useSetAccountPermission,
} from "@/lib/api";
import { cn } from "@/lib/utils";

import { usePendingIdSet } from "./use-pending-id-set";

interface PermissionGridProps {
  accountId: string;
  data: AdminAccountPermissions;
}

// The effect an admin can assign to a cell. Approvable capabilities add the
// third `allow_with_approval` state on top of the plain allow/deny switch.
type GrantEffect = NonNullable<AdminPermissionUpdate["effect"]>;

const APPROVAL_OPTIONS: { value: GrantEffect; label: string }[] = [
  { value: "allow", label: "Allow" },
  { value: "allow_with_approval", label: "Require approval" },
  { value: "deny", label: "Deny" },
];

// Per-cell pending/failed state is keyed by scope + capability so two cells
// toggled at once each track their own in-flight/error status independently.
const cellKey = (contactId: string | null, cap: AdminCapability) =>
  `${contactId ?? "__account__"}:${cap.record_type}:${cap.action}`;

export function PermissionGrid({ accountId, data }: PermissionGridProps) {
  const setPermission = useSetAccountPermission(accountId);
  const pending = usePendingIdSet();
  const failed = usePendingIdSet();

  const { capabilities, account_defaults, contacts } = data;

  // Own each cell's pending/failed lifecycle via its own mutateAsync promise
  // rather than mutate-scoped callbacks. All cells share one MutationObserver,
  // and a rapid second toggle detaches the observer from the first mutation and
  // overwrites its mutate-scoped onError/onSettled — so a superseded cell's
  // callbacks never fire and its spinner would hang. The per-call promise
  // settles independently. The cache write + guarded invalidation stay on the
  // useMutation options, which still run for every execution.
  const applyUpdate = async (key: string, update: AdminPermissionUpdate) => {
    pending.add(key);
    failed.remove(key);
    try {
      await setPermission.mutateAsync(update);
    } catch {
      failed.add(key);
    } finally {
      pending.remove(key);
    }
  };

  const handleSetEffect = (contactId: string | null, cap: AdminCapability, effect: GrantEffect) => {
    void applyUpdate(cellKey(contactId, cap), {
      contact_id: contactId,
      record_type: cap.record_type,
      action: cap.action,
      effect,
    });
  };

  const handleClear = (contactId: string | null, cap: AdminCapability) => {
    void applyUpdate(cellKey(contactId, cap), {
      contact_id: contactId,
      record_type: cap.record_type,
      action: cap.action,
      effect: null,
    });
  };

  // Columns are driven by `capabilities`; each row's grants align 1:1 with it
  // by index (backend contract), so we look the grant up positionally.
  const renderCells = (
    grants: AdminGrantState[],
    contactId: string | null,
    scopeName: string,
    inheritLabel: string,
  ) =>
    capabilities.map((cap, index) => {
      const grant = grants[index];
      if (!grant) {
        return null;
      }
      const key = cellKey(contactId, cap);
      return (
        <td key={`${cap.record_type}:${cap.action}`} className="px-4 py-3">
          <PermissionCell
            capability={cap}
            grant={grant}
            scopeName={scopeName}
            inheritLabel={inheritLabel}
            isPending={pending.ids.has(key)}
            isFailed={failed.ids.has(key)}
            onSetEffect={(effect) => handleSetEffect(contactId, cap, effect)}
            onClear={() => handleClear(contactId, cap)}
          />
        </td>
      );
    });

  const columnCount = capabilities.length + 1;

  return (
    <div className="bg-card border-border overflow-hidden rounded-lg border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-warm-gray-01 border-border border-b">
            <tr>
              <th className="text-muted px-4 py-3 text-left text-[10px] font-medium tracking-[0.16em] uppercase">
                Person
              </th>
              {capabilities.map((cap) => {
                // `inherit_account_default === false` (JJFW-803) means an
                // account-level allow acts as a self-service ceiling rather than
                // a value users inherit — flag the column so J&J reads it right.
                const isCeiling = cap.inherit_account_default === false;
                return (
                  <th
                    key={`${cap.record_type}:${cap.action}`}
                    className="text-muted px-4 py-3 text-center text-[10px] font-medium tracking-[0.16em] uppercase"
                  >
                    <div className="flex flex-col items-center">
                      <span className="flex items-center gap-1">
                        {cap.label}
                        {isCeiling && (
                          <span
                            title="Account row sets a ceiling, not an inherited default: an account-level allow is the maximum a customer admin can self-grant; it is not inherited by users."
                            className="inline-flex"
                          >
                            <ArrowUpToLine
                              className="text-muted h-3 w-3"
                              aria-label="Account row sets a ceiling, not an inherited default"
                            />
                          </span>
                        )}
                      </span>
                      <span className="text-muted font-mono text-[10px] tracking-normal normal-case">
                        {cap.record_type}/{cap.action}
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr className="border-border bg-warm-gray-01/60 border-b">
              <td
                colSpan={columnCount}
                className="text-muted px-4 py-2 text-[10px] font-medium tracking-[0.16em] uppercase"
              >
                Account defaults
              </td>
            </tr>
            <tr className="border-border border-b">
              <td className="px-4 py-3 font-medium">All users</td>
              {renderCells(account_defaults, null, "account default", "default")}
            </tr>

            <tr className="border-border bg-warm-gray-01/60 border-b">
              <td
                colSpan={columnCount}
                className="text-muted px-4 py-2 text-[10px] font-medium tracking-[0.16em] uppercase"
              >
                People
              </td>
            </tr>
            {contacts.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="text-muted px-4 py-8 text-center text-sm">
                  No people at this account yet.
                </td>
              </tr>
            ) : (
              contacts.map((contact) => (
                <tr
                  key={contact.contact_id}
                  className={cn(
                    "border-border border-b last:border-0",
                    !contact.active && "opacity-60",
                  )}
                >
                  <td className="px-4 py-3 align-top">
                    <div className="flex flex-col">
                      <span className="flex flex-wrap items-center gap-2 font-medium">
                        {contact.full_name}
                        {contact.role && <StatusPill>{contact.role}</StatusPill>}
                        {!contact.active && <StatusPill>inactive</StatusPill>}
                      </span>
                      {contact.email && <span className="text-muted text-xs">{contact.email}</span>}
                    </div>
                  </td>
                  {renderCells(contact.grants, contact.contact_id, contact.full_name, "inherited")}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface PermissionCellProps {
  capability: AdminCapability;
  grant: AdminGrantState;
  scopeName: string;
  inheritLabel: string;
  isPending: boolean;
  isFailed: boolean;
  onSetEffect: (effect: GrantEffect) => void;
  onClear: () => void;
}

function PermissionCell({
  capability,
  grant,
  scopeName,
  inheritLabel,
  isPending,
  isFailed,
  onSetEffect,
  onClear,
}: PermissionCellProps) {
  // A non-null `effect` means this scope sets the value explicitly; otherwise
  // the resolved `effective` value is inherited from the default / baseline.
  const isExplicit = grant.effect != null;
  const isCustomerAuthored = grant.authored_by === "customer";
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex items-center gap-1.5">
        {isPending && <Loader2 className="text-muted h-3 w-3 animate-spin" />}
        {capability.approvable ? (
          // Approvable capabilities (orders/create) get a three-state control:
          // orders can be allowed outright, allowed but parked for sign-off, or
          // denied. radix returns the raw string value, so narrow it back.
          <Select
            value={grant.effective}
            disabled={isPending}
            onValueChange={(value) => onSetEffect(value as GrantEffect)}
          >
            <SelectTrigger
              size="sm"
              className={cn(
                "w-[132px] px-2 text-xs",
                grant.effective === "allow_with_approval" && "text-jj-orange-05",
              )}
              aria-label={`${capability.label} for ${scopeName}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {APPROVAL_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value} className="text-xs">
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Switch
            size="sm"
            checked={grant.effective === "allow"}
            disabled={isPending}
            onCheckedChange={(enabled) => onSetEffect(enabled ? "allow" : "deny")}
            aria-label={`${capability.label} for ${scopeName}`}
          />
        )}
      </div>
      {isExplicit ? (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onClear}
            disabled={isPending}
            className="text-accent hover:text-accent/80 flex items-center gap-0.5 text-xs disabled:opacity-50"
            aria-label={`Clear ${capability.label} override for ${scopeName}`}
          >
            custom
            <X className="h-3 w-3" />
          </button>
          {isCustomerAuthored && (
            <StatusPill tone="violet" title="Set by customer admin — capped at the account ceiling">
              customer
            </StatusPill>
          )}
        </div>
      ) : (
        <span className="text-muted text-xs">{inheritLabel}</span>
      )}
      {isFailed && <span className="text-destructive text-xs">failed</span>}
    </div>
  );
}
